// Who is using the app, what they may see, and what kind of device they are on.
// Permissions are rows (permission, role_permission, user_permission); nothing in code
// decides who sees a price.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { refresh, setWritable, useRows, type Row } from './api';
import { REVIEW, supabase } from './supabase';

export type Device = 'desk' | 'tablet' | 'phone';
export const ROLES = ['owner', 'office', 'shop', 'contractor', 'client'] as const;

/**
 * Which roles the database lets change rows. Today only the owner has a write policy (owner_all on
 * every ug table), so this is the one place that says so: when a write policy for another role
 * lands (Bennett bringing items into jobs), this line changes with it and every screen follows.
 */
export function roleWrites(role: string): boolean {
  return role === 'owner';
}

interface Session {
  loading: boolean;
  review: boolean;
  /** Signed in but no person row carries this email yet. */
  notSetUp: boolean;
  needsSignIn: boolean;
  user: Row | null;
  role: string;
  viewAs: string | null;
  setViewAs: (role: string | null) => void;
  can: (key: string) => boolean;
  /** Whether this login may change rows at all: false is a read-only login (an AI employee today). */
  canWrite: boolean;
  /** May see the screen (the permission row) and may change what is on it. The one test a screen's editing controls hang off. */
  canEdit: (key: string) => boolean;
  device: Device;
  frame: 'rail' | 'bottom_bar';
  setting: <T = unknown>(key: string, fallback?: T) => T;
  signOut: () => Promise<void>;
}

const Ctx = createContext<Session | null>(null);

function deviceNow(): Device {
  const w = window.innerWidth;
  if (w < 720) return 'phone';
  if (w < 1100 || (window.matchMedia('(pointer: coarse)').matches && w < 1400)) return 'tablet';
  return 'desk';
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [authId, setAuthId] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(REVIEW);
  const [device, setDevice] = useState<Device>(deviceNow);
  const [viewAs, setViewAsState] = useState<string | null>(() => (REVIEW ? window.localStorage.getItem('zg.viewAs') : null));

  useEffect(() => {
    const on = () => setDevice(deviceNow());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  useEffect(() => {
    if (REVIEW) return;
    let live = true;
    const take = async (id: string | null) => {
      if (id) await supabase.rpc('claim_user');
      if (!live) return;
      setAuthId(id);
      setAuthReady(true);
      refresh('user');
    };
    void supabase.auth.getSession().then(({ data }) => take(data.session?.user.id ?? null));
    const sub = supabase.auth.onAuthStateChange((_e, s) => void take(s?.user.id ?? null));
    return () => {
      live = false;
      sub.data.subscription.unsubscribe();
    };
  }, []);

  const signedIn = REVIEW || authId !== null;
  const users = useRows('user', { enabled: signedIn });
  const perms = useRows('permission', { enabled: signedIn });
  const rolePerms = useRows('role_permission', { enabled: signedIn });
  const userPerms = useRows('user_permission', { enabled: signedIn });
  const settings = useRows('setting', { enabled: signedIn });

  const user = useMemo(() => {
    if (REVIEW) return users.rows.filter((u) => u.role === 'owner' && u.active).sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)))[0] ?? null;
    return users.rows.find((u) => u.auth_user_id === authId) ?? null;
  }, [users.rows, authId]);

  const role = viewAs ?? user?.role ?? 'client';

  const granted = useMemo(() => {
    const keyOf = new Map(perms.rows.map((p) => [p.id, p.key as string]));
    const set = new Set<string>();
    for (const rp of rolePerms.rows) if (rp.role === role) set.add(keyOf.get(rp.permission_id) ?? '');
    // a person's own additions and removals apply to their real role, not to a previewed one
    if (!viewAs && user) {
      for (const up of userPerms.rows) {
        if (up.user_id !== user.id) continue;
        const key = keyOf.get(up.permission_id) ?? '';
        if (up.granted) set.add(key);
        else set.delete(key);
      }
    }
    return set;
  }, [perms.rows, rolePerms.rows, userPerms.rows, role, viewAs, user]);

  const can = useCallback((key: string) => granted.has(key), [granted]);
  const canWrite = roleWrites(role);
  const canEdit = useCallback((key: string) => canWrite && granted.has(key), [granted, canWrite]);
  // the write helpers refuse a write from a read-only login before it reaches the database, with one clear message
  useEffect(() => {
    setWritable(canWrite);
  }, [canWrite]);

  const setting = useCallback(
    <T,>(key: string, fallback?: T): T => {
      const row = settings.rows.find((s) => s.key === key);
      return (row ? row.value : fallback) as T;
    },
    [settings.rows],
  );

  const frameSetting = setting<Record<string, string>>('frame_by_device', {});
  const frame = (frameSetting?.[device] ?? (device === 'phone' ? 'bottom_bar' : 'rail')) === 'bottom_bar' ? 'bottom_bar' : 'rail';

  const value: Session = {
    loading: !authReady || (signedIn && (users.loading || perms.loading || rolePerms.loading || settings.loading)),
    review: REVIEW,
    needsSignIn: authReady && !signedIn,
    notSetUp: authReady && signedIn && !users.loading && !user,
    user,
    role,
    viewAs,
    setViewAs: (r) => {
      setViewAsState(r);
      if (r) window.localStorage.setItem('zg.viewAs', r);
      else window.localStorage.removeItem('zg.viewAs');
    },
    can,
    canWrite,
    canEdit,
    device,
    frame,
    setting,
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}
